import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCorners, useDroppable,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import { canAssignCrmTickets } from '../../../utils/crmPermissions';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { useToast } from '../../../hooks/use-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import CrmTicketPriorityBadge from './CrmTicketPriorityBadge';
import { Badge } from '../../ui/badge';

const COLUMN_WIDTH = 'w-[280px] min-w-[280px]';
const OVERLAY_WIDTH = 'w-[264px]';

function employeeLabel(emp) {
  const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
  return name ? `${name} (${emp.employee_code})` : emp.employee_code;
}

function hasAssignee(ticket) {
  const id = ticket?.assigned_employee_id;
  return id != null && Number(id) > 0;
}

function TicketCard({ ticket, isDragging = false, isOverlay = false }) {
  const cardClass = [
    'border-border min-w-0 overflow-hidden transition-shadow',
    isOverlay
      ? `${OVERLAY_WIDTH} cursor-grabbing shadow-xl scale-[1.02] opacity-100 ring-1 ring-border`
      : isDragging
        ? 'w-full cursor-grabbing border-dashed border-muted-foreground/40 bg-muted/40 shadow-none opacity-40'
        : 'w-full cursor-grab hover:shadow-md active:cursor-grabbing',
  ].join(' ');

  return (
    <Card className={cardClass}>
      <CardContent className={`p-3 space-y-2 min-w-0 ${isDragging && !isOverlay ? 'invisible' : ''}`}>
        <div className="flex justify-between gap-2 min-w-0">
          <Link
            to={`/crm/tickets/${ticket.id}`}
            className="text-xs font-mono text-primary hover:underline truncate min-w-0"
            onClick={(e) => e.stopPropagation()}
          >
            {ticket.ticket_code}
          </Link>
          <CrmTicketPriorityBadge priority={ticket.priority} />
        </div>
        <p className="text-sm font-medium line-clamp-2 break-words">{ticket.title}</p>
        <p className="text-xs text-muted-foreground truncate">{ticket.assigned_to_name || 'Unassigned'}</p>
        {ticket.due_date && (
          <p className="text-xs text-muted-foreground truncate">Due {ticket.due_date.slice(0, 10)}</p>
        )}
        {ticket.labels?.length > 0 && (
          <div className="flex flex-wrap gap-1 min-w-0">
            {ticket.labels.map((l) => (
              <Badge
                key={l.id}
                variant="outline"
                className="text-[10px] px-1 py-0 max-w-full truncate"
                style={{ borderColor: l.color, color: l.color }}
              >
                {l.name}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SortableTicket({ ticket }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `ticket-${ticket.id}`,
    data: { ticket, type: 'ticket' },
  });
  const style = {
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="mb-2 w-full min-w-0">
      <TicketCard ticket={ticket} isDragging={isDragging} />
    </div>
  );
}

function KanbanColumn({ columnKey, column }) {
  const tickets = column?.tickets || [];
  const status = column?.status || {};
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${columnKey}`,
    data: { type: 'column', status: columnKey },
  });

  return (
    <div ref={setNodeRef} className={`${COLUMN_WIDTH} h-full`} data-column={columnKey}>
      <Card
        className={`border-border h-full transition-colors ${
          isOver ? 'bg-primary/5 ring-2 ring-primary/40 shadow-sm' : ''
        }`}
      >
        <CardHeader className="py-3 px-4" style={{ borderTop: `3px solid ${status.color || '#6366f1'}` }}>
          <CardTitle className="text-sm flex justify-between gap-2 min-w-0">
            <span className="truncate">{status.label || columnKey}</span>
            <span className="text-muted-foreground font-normal shrink-0">{tickets.length}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 max-h-[70vh] overflow-y-auto overflow-x-hidden">
          <SortableContext items={tickets.map((t) => `ticket-${t.id}`)} strategy={verticalListSortingStrategy}>
            {tickets.map((t) => (
              <SortableTicket key={t.id} ticket={t} />
            ))}
          </SortableContext>
        </CardContent>
      </Card>
    </div>
  );
}

function findColumnKey(columns, ticketId) {
  for (const [key, col] of Object.entries(columns)) {
    if ((col.tickets || []).some((t) => t.id === ticketId)) return key;
  }
  return null;
}

function resolveOverStatus(over, columns) {
  if (!over) return null;
  if (over.data?.current?.type === 'column') return over.data.current.status;
  if (over.data?.current?.type === 'ticket') return findColumnKey(columns, over.data.current.ticket.id);
  if (String(over.id).startsWith('column-')) return String(over.id).replace('column-', '');
  if (String(over.id).startsWith('ticket-')) {
    const id = Number(String(over.id).replace('ticket-', ''));
    return findColumnKey(columns, id);
  }
  return null;
}

/** Move ticket into targetStatus; optionally insert before overTicketId. Returns null if no change. */
function moveTicket(columns, ticketId, targetStatus, overTicketId = null) {
  const sourceKey = findColumnKey(columns, ticketId);
  if (!sourceKey || !columns[targetStatus]) return null;

  const sourceTickets = columns[sourceKey].tickets || [];
  const ticketIndex = sourceTickets.findIndex((t) => t.id === ticketId);
  if (ticketIndex < 0) return null;

  const ticket = { ...sourceTickets[ticketIndex], status: targetStatus };

  if (sourceKey === targetStatus) {
    if (overTicketId == null || overTicketId === ticketId) return null;
    const without = sourceTickets.filter((t) => t.id !== ticketId);
    let insertAt = without.findIndex((t) => t.id === overTicketId);
    if (insertAt < 0) insertAt = without.length;
    const nextTickets = [...without.slice(0, insertAt), ticket, ...without.slice(insertAt)];
    const sameOrder = nextTickets.every((t, i) => t.id === sourceTickets[i]?.id);
    if (sameOrder) return null;
    return {
      ...columns,
      [sourceKey]: { ...columns[sourceKey], tickets: nextTickets },
    };
  }

  const destTickets = [...(columns[targetStatus].tickets || [])];
  let insertAt = destTickets.length;
  if (overTicketId != null) {
    const idx = destTickets.findIndex((t) => t.id === overTicketId);
    if (idx >= 0) insertAt = idx;
  }

  return {
    ...columns,
    [sourceKey]: {
      ...columns[sourceKey],
      tickets: sourceTickets.filter((t) => t.id !== ticketId),
    },
    [targetStatus]: {
      ...columns[targetStatus],
      tickets: [...destTickets.slice(0, insertAt), ticket, ...destTickets.slice(insertAt)],
    },
  };
}

function mergeTicketIntoColumns(columns, ticket) {
  if (!ticket?.id) return columns;
  const next = { ...columns };
  for (const [key, col] of Object.entries(next)) {
    const tickets = col.tickets || [];
    const idx = tickets.findIndex((t) => t.id === ticket.id);
    if (idx < 0) continue;
    const merged = { ...tickets[idx], ...ticket };
    next[key] = {
      ...col,
      tickets: [...tickets.slice(0, idx), merged, ...tickets.slice(idx + 1)],
    };
  }
  return next;
}

export default function CrmTicketKanban({ relatedEmployeeId = '' }) {
  const { user } = useAuth();
  const canAssign = canAssignCrmTickets(user);
  const [columns, setColumns] = useState({});
  const [activeTicket, setActiveTicket] = useState(null);
  const [assignDialog, setAssignDialog] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const columnsRef = useRef({});
  const snapshotRef = useRef(null);
  const originalStatusRef = useRef(null);
  const lastOverIdRef = useRef(null);
  const assignRollbackRef = useRef(null);
  const { toast } = useToast();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const applyColumns = useCallback((next) => {
    columnsRef.current = next;
    setColumns(next);
  }, []);

  const load = useCallback(() => {
    const params = {};
    if (relatedEmployeeId) params.related_employee_id = relatedEmployeeId;
    api.get('/crm/tickets/kanban', { params })
      .then((r) => applyColumns(r.data?.data?.columns || {}))
      .catch(() => applyColumns({}));
  }, [relatedEmployeeId, applyColumns]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!assignDialog || !canAssign) {
      setEmployees([]);
      return;
    }
    api.get('/crm/employees')
      .then((r) => setEmployees((r.data?.data?.items || []).filter((e) => e.status === 'active')))
      .catch(() => setEmployees([]));
  }, [assignDialog, canAssign]);

  const rollbackAssignDialog = useCallback(() => {
    if (assignRollbackRef.current) {
      applyColumns(assignRollbackRef.current);
      assignRollbackRef.current = null;
    }
    setAssignDialog(null);
    setSelectedAssigneeId('');
    setAssignSaving(false);
  }, [applyColumns]);

  const handleAssignDialogOpenChange = (open) => {
    if (!open) {
      if (assignSaving) return;
      rollbackAssignDialog();
    }
  };

  const handleConfirmAssign = async () => {
    if (!assignDialog || !selectedAssigneeId) return;
    setAssignSaving(true);
    try {
      const res = await api.put(`/crm/tickets/${assignDialog.id}`, {
        assigned_employee_id: Number(selectedAssigneeId),
        status: 'assigned',
      });
      const updated = res.data?.data;
      if (updated) {
        applyColumns(mergeTicketIntoColumns(columnsRef.current, updated));
      }
      assignRollbackRef.current = null;
      setAssignDialog(null);
      setSelectedAssigneeId('');
    } catch (e) {
      if (assignRollbackRef.current) {
        applyColumns(assignRollbackRef.current);
        assignRollbackRef.current = null;
      }
      setAssignDialog(null);
      setSelectedAssigneeId('');
      toast({
        title: 'Assign failed',
        description: e.response?.data?.message || e.message,
        variant: 'destructive',
      });
    } finally {
      setAssignSaving(false);
    }
  };

  const handleDragStart = (event) => {
    const ticket = event.active?.data?.current?.ticket;
    if (!ticket) return;
    setActiveTicket(ticket);
    snapshotRef.current = columnsRef.current;
    originalStatusRef.current = findColumnKey(columnsRef.current, ticket.id) || ticket.status;
    lastOverIdRef.current = null;
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over || !active?.data?.current?.ticket) return;
    if (lastOverIdRef.current === over.id) return;
    lastOverIdRef.current = over.id;

    const ticketId = active.data.current.ticket.id;
    const overTicketId = over.data?.current?.type === 'ticket' ? over.data.current.ticket.id : null;
    const prev = columnsRef.current;
    const targetStatus = resolveOverStatus(over, prev);
    if (!targetStatus) return;

    const next = moveTicket(prev, ticketId, targetStatus, overTicketId);
    if (next) applyColumns(next);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveTicket(null);
    lastOverIdRef.current = null;

    const ticket = active?.data?.current?.ticket;
    const pendingOriginal = originalStatusRef.current;
    const rollback = snapshotRef.current;
    originalStatusRef.current = null;
    snapshotRef.current = null;

    if (!ticket) return;

    let next = columnsRef.current;
    if (over) {
      const targetStatus = resolveOverStatus(over, next);
      if (targetStatus) {
        const overTicketId = over.data?.current?.type === 'ticket' ? over.data.current.ticket.id : null;
        const moved = moveTicket(next, ticket.id, targetStatus, overTicketId);
        if (moved) next = moved;
      }
    }
    applyColumns(next);

    const currentStatus = findColumnKey(next, ticket.id);
    if (!currentStatus || currentStatus === pendingOriginal) return;

    const needsAssignee = currentStatus === 'assigned' && !hasAssignee(ticket);
    if (needsAssignee) {
      if (!canAssign) {
        if (rollback) applyColumns(rollback);
        toast({
          title: 'Assignee required',
          description: 'You do not have permission to assign tickets.',
          variant: 'destructive',
        });
        return;
      }
      assignRollbackRef.current = rollback;
      setSelectedAssigneeId('');
      setAssignDialog(ticket);
      return;
    }

    api.patch(`/crm/tickets/${ticket.id}/status`, { status: currentStatus }).catch((e) => {
      if (rollback) applyColumns(rollback);
      toast({ title: 'Move failed', description: e.response?.data?.message, variant: 'destructive' });
    });
  };

  const handleDragCancel = () => {
    setActiveTicket(null);
    lastOverIdRef.current = null;
    if (snapshotRef.current) {
      applyColumns(snapshotRef.current);
      snapshotRef.current = null;
    }
    originalStatusRef.current = null;
  };

  const selfEmployee = employees.find((e) => Number(e.user_id) === Number(user?.id));
  const otherEmployees = selfEmployee
    ? employees.filter((e) => Number(e.id) !== Number(selfEmployee.id))
    : employees;

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="flex gap-3 overflow-x-auto pb-4">
          {Object.entries(columns).map(([key, col]) => (
            <div key={key} id={`column-${key}`} className={`${COLUMN_WIDTH} flex-shrink-0`}>
              <KanbanColumn columnKey={key} column={col} />
            </div>
          ))}
        </div>
        <DragOverlay dropAnimation={null}>
          {activeTicket ? <TicketCard ticket={activeTicket} isOverlay /> : null}
        </DragOverlay>
      </DndContext>

      <Dialog open={!!assignDialog} onOpenChange={handleAssignDialogOpenChange}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Assign ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Select an employee to move{' '}
              <span className="font-mono text-foreground">{assignDialog?.ticket_code}</span>
              {' '}to Assigned.
            </p>
            <div>
              <Label>Assign To</Label>
              <Select value={selectedAssigneeId} onValueChange={setSelectedAssigneeId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {selfEmployee && (
                    <SelectItem value={String(selfEmployee.id)}>Assign to me</SelectItem>
                  )}
                  {otherEmployees.map((e) => (
                    <SelectItem key={e.id} value={String(e.id)}>{employeeLabel(e)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={rollbackAssignDialog} disabled={assignSaving}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmAssign}
              disabled={!selectedAssigneeId || assignSaving}
            >
              {assignSaving ? 'Assigning…' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
